/**
 * 주문 상태 관리 관련 함수
 * 기존 order.ts에서 분리
 */

import { supabase } from '@/lib/supabase';
import type { Order } from '@/types';

/**
 * 주문 상태 업데이트
 */
export async function updateOrderStatus(
  orderId: string,
  newStatus: Order['status'],
  _updatedBy?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('orders')
      .update({ 
        status: newStatus,
        updated_at: new Date().toISOString(),
        ...(newStatus === 'ACCEPTED' && { accepted_at: new Date().toISOString() }),
        ...(newStatus === 'READY' && { 
          ready_at: new Date().toISOString()
        }),
        ...(newStatus === 'COMPLETED' && { actual_pickup_time: new Date().toISOString() })
      })
      .eq('id', orderId);

    if (error) {
      return { success: false, error: error.message };
    }

    // 카카오톡 알림 발송 (상태에 따라)
    if (newStatus === 'ACCEPTED' || newStatus === 'READY' || newStatus === 'CANCELLED') {
      await sendOrderNotification(orderId, newStatus);
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating order status:', error);
    return { success: false, error: '주문 상태 업데이트에 실패했습니다.' };
  }
}

/**
 * 주문 알림 발송 (private)
 */
export async function sendOrderNotification(
  orderId: string, 
  status: Order['status'],
  isSellerRejection: boolean = false
): Promise<void> {
  try {
    let notificationType: 'order_accepted' | 'order_cancelled' | 'order_rejected' | 'order_ready' | null = null;
    
    switch(status) {
      case 'ACCEPTED':
        notificationType = 'order_accepted';
        break;
      case 'CANCELLED':
        notificationType = isSellerRejection ? 'order_rejected' : 'order_cancelled';
        break;
      case 'READY':
        notificationType = 'order_ready';
        break;
      default:
        return; // 알림을 보내지 않는 상태
    }

    const { data, error } = await supabase.functions.invoke('send-order-notification', {
      body: { 
        orderId, 
        notificationType 
      }
    });

    if (error) {
      console.error('알림 전송 실패:', error);
      // 알림 실패해도 주문 처리는 계속 진행
    } else {
      console.log('알림 전송 성공:', data);
    }
  } catch (error) {
    console.error('알림 전송 중 오류:', error);
    // 알림 실패해도 주문 처리는 계속 진행
  }
}

/**
 * Fallback을 포함한 주문 상태 업데이트 (private)
 */
export async function updateOrderStatusWithFallback(
  orderId: string,
  status: Order['status'],
  reason?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // 직접 DB 업데이트
    const { error } = await supabase
      .from('orders')
      .update({ 
        status: status,
        updated_at: new Date().toISOString(),
        ...(status === 'CANCELLED' && { 
          cancelled_at: new Date().toISOString(),
          cancellation_reason: reason || '고객 요청 취소'
        })
      })
      .eq('id', orderId);

    if (error) {
      return { success: false, error: error.message };
    }

    // payments 테이블도 업데이트
    await supabase
      .from('payments')
      .update({ 
        status: 'cancelled',
        cancelled_at: new Date().toISOString(),
        cancellation_reason: reason || '고객 요청 취소'
      })
      .eq('order_id', orderId);

    // 알림 발송 시도 (실패해도 무시)
    sendOrderNotification(orderId, status).catch(console.error);

    return { success: true };
  } catch (error) {
    console.error('Fallback update failed:', error);
    return { success: false, error: '주문 상태 업데이트 실패' };
  }
}

/**
 * 내부 주문 취소 (private)
 */
export async function cancelOrderInternal(
  orderId: string,
  _reason: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Update order status to cancelled
    const { error } = await supabase
      .from('orders')
      .update({ 
        status: 'CANCELLED',
        payment_status: 'FAILED',
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error cancelling order internally:', error);
    return { success: false, error: '주문 취소 중 오류가 발생했습니다.' };
  }
}